import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import leaf from '../../assets/images/leaf.svg';
import Pagination from '../../components/Pagination';
import { formatDate } from '../../utils/formatDate';
import AdminMembersService from '../../api/service/admin/AdminMembersService';
import { MembersListData } from '../../api/service/admin/AdminMembersInterface';
import DeletedReasonPopup from './DeletedReasonPopup';

const NoticeTabWrapper = styled.div``;

const NoticeHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  justify-content: space-between;
  gap: 10px;
`;

const TextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  img {
    width: 20px;
  }
`;

const StyledSelect = styled.select`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
  background-color: #fff;

  &:focus {
    border-color: #85c1a9;
    outline: none;
  }

  /* 반응형 스타일 */
  @media (max-width: 768px) {
    font-size: 12px; /* 작은 화면에서는 글씨 크기를 줄임 */
    padding: 8px; /* 작은 화면에서는 패딩을 줄임 */
  }

  @media (max-width: 480px) {
    font-size: 10px; /* 더 작은 화면에서는 글씨 크기를 더 줄임 */
    padding: 6px; /* 더 작은 화면에서는 패딩을 더 줄임 */
    border-radius: 2px; /* 작은 화면에서는 둥근 모서리를 더 작게 */
  }
`;
const TableContainer = styled.div`
  overflow-x: auto;
  min-height: 481px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
`;

const TableBox = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: white;

  tr {
    border-bottom: 1px solid #ddd;
  }

  tr:nth-child(12) {
    border-bottom: none; /* 마지막 행의 border 제거 */
  }

  th,
  td {
    font-size: 16px;
    padding: 10px;
    text-align: center;
    vertical-align: middle; /* 세로 정렬 */
    white-space: nowrap; /* 줄바꿈 방지 */
  }

  th {
    background-color: #85c1a9;
    color: white;
    font-weight: 500;
  }

  th:first-child {
    border-top-left-radius: 10px;
  }

  th:last-child {
    border-top-right-radius: 10px;
  }

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;

    tr {
      border-bottom: 1px solid #ddd;
    }

    th,
    td {
      font-size: 12px;
      padding: 10px;
      text-align: center;
      vertical-align: middle; /* 세로 정렬 */
      white-space: nowrap; /* 줄바꿈 방지 */
    }

    th {
      font-weight: 400;
    }

    th:first-child {
      border-top-left-radius: 0px;
    }

    th:last-child {
      border-top-right-radius: 0px;
    }
  }
`;

function AdminTab2() {
  const [notices, setNotices] = useState<MembersListData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [permissionType, setPermissionType] = useState<string>('SELLER'); // 초기값
  const [isDeleted, setIsDeleted] = useState<boolean>(false); // 초기값 (가입회원)

  // Popup states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<MembersListData | null>(
    null
  );

  const handlePermissionChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = event.target.value;
    setPermissionType(value);
    fetchMembers(0, value, isDeleted); // 첫 페이지 데이터 로드
  };

  const handleIsDeletedChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = event.target.value === 'true'; // 문자열을 Boolean으로 변환
    setIsDeleted(value);
    fetchMembers(0, permissionType, value); // 첫 페이지 데이터 로드
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchMembers(page, permissionType, isDeleted);
  };

  useEffect(() => {
    fetchMembers(0, permissionType, isDeleted); // 초기 데이터 로드
  }, []); // 의존성 배열 비워 두어 처음 한 번 실행

  const fetchMembers = async (
    currentPage: number,
    permissionType: string,
    isDeleted: boolean
  ) => {
    try {
      const response = await AdminMembersService.getMembersList(
        isDeleted, // Boolean 변환: 가입회원일 때 true
        currentPage,
        12,
        permissionType
      );
      setNotices(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error(err);
      alert(err);
    }
  };

  const openModal = (member: MembersListData) => {
    setSelectedMember(member);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setSelectedMember(null);
    setIsModalOpen(false);
  };

  return (
    <NoticeTabWrapper>
      <NoticeHeader>
        <TextContainer>
          <img src={leaf} alt="leaf" />
          <h1>회원정보 조회</h1>
        </TextContainer>
        <TextContainer>
          <StyledSelect name="isDeleted" onChange={handleIsDeletedChange}>
            <option value="false">가입회원</option>
            <option value="true">탈퇴회원</option>
          </StyledSelect>
          <StyledSelect name="permissionType" onChange={handlePermissionChange}>
            <option value="SELLER">판매자</option>
            <option value="BUYER">구매자</option>
          </StyledSelect>
        </TextContainer>
      </NoticeHeader>
      <TableContainer>
        <TableBox>
          <Table>
            <thead>
              <tr>
                <th>NO</th>
                <th>이름</th>
                <th>아이디</th>
                <th>가입날짜</th>
                <th>주소</th>
                <th>이메일</th>
                {permissionType === 'SELLER' && <th>사업자명</th>}
                {permissionType === 'SELLER' && <th>사업자번호</th>}
                {permissionType === 'SELLER' && <th>대표자</th>}
                {permissionType === 'SELLER' && <th>사업장 주소</th>}
                {permissionType === 'SELLER' && <th>계좌번호</th>}
                {isDeleted === true && <th>탈퇴 날짜</th>}
              </tr>
            </thead>
            <tbody>
              {notices.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{ textAlign: 'center', padding: '20px' }}
                  >
                    데이터가 없습니다.
                  </td>
                </tr>
              ) : (
                notices.map((item, index) => (
                  <tr
                    key={item.identifier}
                    onClick={() => isDeleted && openModal(item)}
                  >
                    <td>{index + 1}</td>
                    <td>{item.name}</td>
                    <td>{item.identifier}</td>
                    <td>{formatDate(item.regDate)}</td>
                    <td>{item.address}</td>
                    <td>{item.email}</td>
                    {permissionType === 'SELLER' && (
                      <td>{item.businessInfo?.businessName}</td>
                    )}
                    {permissionType === 'SELLER' && (
                      <td>{item.businessInfo?.businessNumber}</td>
                    )}
                    {permissionType === 'SELLER' && (
                      <td>{item.businessInfo?.businessRepresentativeName}</td>
                    )}
                    {permissionType === 'SELLER' && (
                      <td>
                        {item.businessInfo?.businessAddress.addressString}
                      </td>
                    )}
                    {permissionType === 'SELLER' && (
                      <td>{`${item.businessInfo?.businessAccount.accountBank}-${item.businessInfo?.businessAccount.accountNumber}`}</td>
                    )}
                    {isDeleted === true && (
                      <td>
                        {item.deletedDate
                          ? formatDate(item.deletedDate)
                          : '없음'}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </TableBox>
      </TableContainer>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />

      {isModalOpen && selectedMember && (
        <DeletedReasonPopup
          title="탈퇴 사유"
          body={selectedMember.deletedReason || '사유 없음'}
          onClose={closeModal}
        />
      )}
    </NoticeTabWrapper>
  );
}

export default AdminTab2;
